import getGroup, { gqlGroupRoomPartyOptions } from '../../Groups/getGroup.api';
import React, {useEffect, useState} from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme
} from '@material-ui/core';
import NumberSpinner from '../../BookingTool/NumberSpinner';
import {makeStyles} from '@material-ui/styles';
import SearchBar from '../../../components/SearchBar';
import {useSnackbar} from 'notistack';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {Group, GroupResourceOption, RoomParty, RoomPartyOption} from '../../../graphql/groups.datatypes';
import anyLoading from '../../../utils/anyLoading';
import {ResourceOptionAction} from '../../../graphql/resources.datatypes';
import deleteRoomPartyOption from './deleteRoomPartyOption.api';
import updateRoomPartyOption from './updateRoomPartyOption.api';
import createRoomPartyOption from './createRoomPartyOption.api';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {amber, blue, lime} from '@material-ui/core/colors';
import getGroupByRef from '../../Groups/getGroupByRef.api';
import {checkEditLevel} from '../../../utils/checkEditLevel';
import moment from 'moment';
import getGroupWithQuery from '../../Groups/getGroupWithQuery.api';


const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    tableButtonsRow: {
        display: 'flex',
        flexDirection: 'row-reverse',
    },
    chipMandatory: {
        backgroundColor: blue[200],
        marginLeft: theme.spacing(1),
    },
    chipAdminOnly: {
        backgroundColor: amber[200],
        marginLeft: theme.spacing(1),
    },
    chipAdminEdited: {
        backgroundColor: lime[200],
        marginLeft: theme.spacing(1),
    },
}));

export const RoomPartyOptionsEditContent = (props: { group: Group, roomParty: RoomParty, isAdmin: boolean, canEdit: boolean, groupRef?: string }) => {
    const classes = useStyles();
    const {group, roomParty, isAdmin, canEdit, groupRef} = props;
    const [optionsFilter, setOptionsFilter] = useState('');
    const [sendCreateRoomPartyOption, createRoomPartyOptionData] = createRoomPartyOption();
    const [sendUpdateRoomPartyOption, updateRoomPartyOptionData] = updateRoomPartyOption();
    const [sendDeleteRoomPartyOption, deleteRoomPartyOptionData] = deleteRoomPartyOption();
    const {enqueueSnackbar} = useSnackbar();
    const loadingData = anyLoading(createRoomPartyOptionData, updateRoomPartyOptionData, deleteRoomPartyOptionData);

    const setRoomPartyOption = (groupOption: GroupResourceOption, newValue: number, option?: RoomPartyOption) => {
        if (newValue > 0) {
            if (option) {
                const sanitisedData = {
                    data: {
                        id: option.id,
                        room_party_id: roomParty!.id,
                        group_resource_option_id: groupOption.id,
                        unit_count: newValue,
                        unit_cost: option.unit_cost!,
                        admin_edited: true,
                    }
                };
                sendUpdateRoomPartyOption({variables: {...sanitisedData}})
                    .then(() => {
                        enqueueSnackbar('Option Updated.', {variant: 'success'});
                    })
                    .catch(() => {
                        enqueueSnackbar('Option Update Failed!', {variant: 'error'});
                    });
            } else {
                const sanitisedData = {
                    data: {
                        room_party_id: roomParty!.id,
                        group_resource_option_id: groupOption.id,
                        unit_count: newValue,
                        unit_cost: groupOption.unitCost!,
                        admin_edited: true,
                    }
                };
                sendCreateRoomPartyOption({variables: sanitisedData})
                    .then(() => {
                        enqueueSnackbar('Option Created.', {variant: 'success'});
                    })
                    .catch(() => {
                        enqueueSnackbar('Create Option Failed!', {variant: 'error'});
                    });
            }
        } else {
            if (option) {
                sendDeleteRoomPartyOption({variables: {id: option.id}})
                    .then(() => {
                        enqueueSnackbar('Option Deleted.', {variant: 'success'});
                    })
                    .catch(() => {
                        enqueueSnackbar('Delete Option Failed!', {variant: 'error'});
                    });
            }
        }
    };

    const checkLevel = checkEditLevel();
    const showPrices = isAdmin || (moment(group.end_date).add(15, 'days').diff(moment(), 'days') > 0);

    return (
        <Card className={classes.container}>
            <LoadingOverlay loading={loadingData.loading}>
                <CardHeader title="Room Options"/>
                <CardContent className={classes.card}>

                    <SearchBar onSearch={(value: string) => setOptionsFilter(value)}/><br/>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Option Name</TableCell>
                                {showPrices && (<TableCell>Option Cost</TableCell>)}
                                <TableCell>Option Set</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {group && group.options && group.options
                                .filter(groupOption =>
                                    groupOption.action_type === ResourceOptionAction.ROOM_RESOURCE_MANDATORY ||
                                    groupOption.action_type === ResourceOptionAction.ROOM_RESOURCE_AVAILABLE
                                )
                                .filter(groupOption => groupOption.option!.internal_name!.indexOf(optionsFilter) !== -1)
                                .filter(groupOption => isAdmin || checkLevel(group.id, groupOption.view_level!))
                                .map(groupOption => {
                                        const currentResource = groupOption.option.resource!;
                                        const remaining = currentResource.consumable ? currentResource.resource_banks![0].remaining! : 999999;
                                        const isAvailable = !currentResource.consumable || (currentResource.consumable && currentResource.resource_banks![0].remaining! > 0);
                                        const roomOption = roomParty.options && roomParty.options.find(checkOption => checkOption.group_resource_option_id! === groupOption.id);
                                        const value = roomOption && roomOption.unit_count > 0 ? roomOption.unit_count : 0;
                                        const isMandatory = groupOption.action_type === ResourceOptionAction.ROOM_RESOURCE_MANDATORY;
                                        const isAdminOnly = groupOption.option && groupOption.option.is_admin_only

                                        return (
                                            <TableRow
                                                hover
                                                key={groupOption.id}
                                            >
                                                <TableCell>
                                                    {groupOption.option.internal_name}
                                                    {isMandatory && (
                                                        <Chip icon={<ErrorOutlineIcon/>} label="rules based only"
                                                              className={classes.chipMandatory} size="small"/>
                                                    )}
                                                    {isAdminOnly && (
                                                        <Chip icon={<ErrorOutlineIcon/>} label="admin only"
                                                              className={classes.chipAdminOnly} size="small"/>
                                                    )}
                                                    {roomOption && roomOption.admin_edited && (
                                                        <Chip icon={<ErrorOutlineIcon/>} label="admin edited"
                                                              className={classes.chipAdminEdited} size="small"/>
                                                    )}
                                                </TableCell>
                                                {showPrices && (<TableCell>${groupOption.unit_cost}</TableCell>)}
                                                <TableCell>
                                                    <NumberSpinner
                                                        value={value}
                                                        min={0} max={remaining}
                                                        disabled={!isAvailable}
                                                        onChange={(newValue) => setRoomPartyOption(groupOption, newValue, roomOption)}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    }
                                )}
                        </TableBody>
                    </Table>
                </CardContent>
            </LoadingOverlay>
        </Card>
    );
};

export const RoomPartyOptionsEdit = (props: { groupId: number, roomParty: RoomParty, isAdmin: boolean, canEdit: boolean, groupRef?: string }) => {
    const {groupId, roomParty, isAdmin, canEdit, groupRef} = props;
    const groupData = isAdmin ? getGroupWithQuery(groupId, gqlGroupRoomPartyOptions) : getGroupByRef(groupRef!);
    const group = isAdmin ? groupData.data && groupData.data.group : groupData.data && groupData.data.groupByRef;
    const {enqueueSnackbar} = useSnackbar();

    useEffect(() => {
        if (groupData.error) {
            enqueueSnackbar(`There was an error loading Group Data`, {variant: 'error'});
        }
    }, [groupData.error]);

    if (groupData.error) return (<div>There was an error loading group data.</div>);
    return (
        <React.Fragment>
            <LoadingOverlay loading={groupData.loading}>
                {group && (
                    <RoomPartyOptionsEditContent group={group} roomParty={roomParty} isAdmin={isAdmin} canEdit={canEdit}
                                                 groupRef={groupRef}/>)}
            </LoadingOverlay>
        </React.Fragment>
    );
};

export default RoomPartyOptionsEdit;
